import axios, { AxiosInstance } from "axios";
import { CORPORATE_BACKOFFICE_PATHS, HMI_PATHS, MADERA_BACKOFFICE_PATHS } from "constants/constants";
import { getClusterIdHMI, getMachineCenterIdHMI, getToken, getUser } from "utilities/storageUtilities";

/**
 * Detects the current application context based on the URL pathname.
 * @returns The context identifier: 'corporate-backoffice', 'madera-backoffice', or 'hmi/...'
 */
const getApplicationContext = (): string => {
    const pathname = window.location.pathname.toLowerCase();

    if (pathname.includes(CORPORATE_BACKOFFICE_PATHS.ROOT_PATH.toLowerCase())) {
        const user = getUser();

        if (user) {
            return `corporate-backoffice/user-${user}`;
        }
        return "corporate-backoffice";
    }
    if (pathname.includes(MADERA_BACKOFFICE_PATHS.ROOT_PATH.toLowerCase())) {
        const user = getUser();

        if (user) {
            return `madera-backoffice/user-${user}`;
        }
        return "madera-backoffice";
    }
    if (pathname.includes(HMI_PATHS.ROOT_PATH.toLowerCase())) {
        const machineCenterIdHMI = getMachineCenterIdHMI();
        const clusterIdHMI = getClusterIdHMI();

        if (pathname.includes(HMI_PATHS.DASHBOARD.toLowerCase()) && machineCenterIdHMI) {
            return `hmi/machine-center-${machineCenterIdHMI.machine_center_id}`;
        }
        if (pathname.includes(HMI_PATHS.CLUSTER.toLowerCase()) && clusterIdHMI) {
            return `hmi/cluster-${clusterIdHMI}`;
        }
        return "hmi";
    }

    return "unknown";
};

/**
 * Generates the value for the X-Madera-Context header for frontend HTTP requests.
 * Format: {context}/{version}
 * Examples:
 * - corporate-backoffice/1.1.1
 * - madera-backoffice/1.1.1
 * - hmi/1.1.1
 */
const getMaderaContextHeaderValue = (): string => {
    const context = getApplicationContext();
    const version = process.env.REACT_APP_VERSION || "0.0.0";
    return `${context}/${version}`;
};

/**
 * Configured axios instance with dynamic X-Madera-Context and Authorization headers.
 * Both are determined on each request so they reflect the current context and session.
 */
const axiosInstance: AxiosInstance = axios.create();

/**
 * True when a request targets our own backend rather than a third-party host.
 *
 * The app JWT is only attached to these. Relative URLs are ours by definition; absolute ones must
 * share an ORIGIN (scheme + host + port) with REACT_APP_API_ROOT — compared parsed, not as a
 * string prefix, or `https://api.x.com.evil.tld` would pass a startsWith against
 * `https://api.x.com` and ship the session token off-origin.
 */
const targetsOwnBackend = (url?: string): boolean => {
    if (!url || !/^[a-z][a-z0-9+.-]*:\/\//i.test(url)) {
        return true;
    }
    const apiRoot = process.env.REACT_APP_API_ROOT;
    if (!apiRoot) {
        return false;
    }
    try {
        return new URL(url).origin === new URL(apiRoot, window.location.href).origin;
    } catch {
        return false;
    }
};

axiosInstance.interceptors.request.use(
    (config) => {
        // axios materializes config.headers as an AxiosHeaders instance before the interceptor
        // chain runs, so `.set()` is always available and normalizes the header name — meaning a
        // call site that already set "Authorization" wins and no duplicate pair is produced.
        config.headers.set("X-Madera-Context", getMaderaContextHeaderValue());

        // OS-2555: identifies browser traffic to the backend's internal-auth middleware.
        const token = getToken();
        if (token && !config.headers.has("authorization") && targetsOwnBackend(config.url)) {
            config.headers.set("authorization", `Bearer ${token}`);
        }

        return config;
    },
    (error) => {
        return Promise.reject(error);
    },
);

export default axiosInstance;
